import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import BackupRestoreModal from '../BackupRestoreModal';
import { track } from '../../utils/analytics';
import {
    shouldOfferRestore, shouldOfferResume, formatTimeAgo, summarizeCloudBackup,
} from '../../utils/cloudBackup';
import { autoResumeRestore } from '../../utils/autoResumeRestore';
import { useToast } from '../../contexts/ToastContext';
import './RestoreFoundPrompt.scss';

/**
 * Two prompts in one card, mounted for the whole session in App.js (desktop
 * only; renders nothing without the backup bridge):
 *
 * - "We found your backup" — the fresh-machine / reinstall moment. Once per
 *   owner: a signed-in user with backup OFF and cloud data waiting. Both
 *   paths out set the durable `restorePromptDismissed` flag, so it can never
 *   nag (`shouldOfferRestore`).
 * - "Your restore didn't finish" — a guided restore started (durable
 *   `restorePending`, set by the wizard at run start) but the app died or
 *   the run failed before completion, and backed-up files are still missing
 *   here (`shouldOfferResume`). Deliberately NOT silenced by
 *   restorePromptDismissed — that flag was set by the accept that started
 *   the unfinished restore. "Not now" clears restorePending; finishing the
 *   restore clears it in the wizard.
 */
function RestoreFoundPrompt() {
    const backupApi = typeof window !== 'undefined' ? window.electron?.backup : null;
    const isAuthenticated = useSelector((s) => !!s.auth?.user?.id);
    const userId = useSelector((s) => s.auth?.user?.id);
    const toast = useToast();
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const [offer, setOffer] = useState(null); // { toolCount, filesTotal, lastAt }
    const [showRestore, setShowRestore] = useState(false);
    const checkedForRef = useRef(null); // owner id we already probed this session

    useEffect(() => {
        if (!backupApi || !isAuthenticated || !userId) return undefined;
        if (checkedForRef.current === userId) return undefined;
        checkedForRef.current = userId;
        let cancelled = false;
        let retryTimer = null;
        let signInWaits = 0;
        const probe = async () => {
            try {
                const status = await backupApi.getStatus();
                if (cancelled || !status || status.error) return;
                // Renderer auth restores ~1s before the main process's owner
                // context on every launch — a not-signed-in status carries
                // DEFAULT settings and must not be trusted. This effect's
                // deps never change again after auth lands, so RETURNING
                // here killed the probe for the whole session: an
                // interrupted restore got no auto-resume and no prompt
                // (MG-1017 — ownerContext landed 600ms after the probe).
                // Wait the gap out instead.
                if (!status.signedIn) {
                    if (signInWaits++ < 10 && !cancelled) retryTimer = setTimeout(probe, 1500);
                    else checkedForRef.current = null; // gave up — a remount may retry
                    return;
                }
                if (status.snapshotRunning || status.files?.state === 'running') return;
                // Cheap local pre-checks before any network. An interrupted
                // restore (restorePending) is a candidate even when the
                // fresh-machine prompt has been dismissed — the dismissal came
                // from accepting the very restore that didn't finish.
                const settings = status.settings;
                const resumeCandidate = !!settings?.restorePending;
                const freshCandidate = !settings?.enabled && !settings?.restorePromptDismissed;
                if (!resumeCandidate && !freshCandidate) return;
                // One preview call carries everything the card says: what the
                // backup holds, which machine it came from, and how much of it
                // is missing here.
                const preview = await backupApi.restorePreview?.();
                if (cancelled || !preview || preview.error) return;
                if (resumeCandidate) {
                    const missing = preview.files?.missing || 0;
                    if (shouldOfferResume({ signedIn: true, settings, missing })) {
                        // The user consented when they STARTED this restore —
                        // finish it silently instead of asking again. The run
                        // is visible (restore toast + Settings card) and
                        // stoppable throughout. Capped at 2 silent attempts:
                        // if resuming is what keeps killing the app, the
                        // third launch shows the prompt instead of looping.
                        const scope = settings.restorePendingScope;
                        const attempts = settings.restoreAutoAttempts || 0;
                        if (scope && attempts < 2 && !status.restore?.active) {
                            await backupApi.setEnabled({ restoreAutoAttempts: attempts + 1 }).catch(() => {});
                            track('backup_restore_auto_resumed', { attempt: attempts + 1, missing });
                            // No announcement card: the app-wide restore
                            // toast appears the moment progress flows — it IS
                            // the announcement (persistent, live), and its
                            // restore card carries a Stop for the user who
                            // quit-as-stop'd last session. Two pop-ups saying
                            // the same thing is one too many.
                            try {
                                const outcome = await autoResumeRestore({
                                    backupApi,
                                    scope,
                                    lastMergeDj: settings.lastMergeDj || {},
                                    notify: (title, message) => toastRef.current?.card?.({ title, message, duration: 7000 }),
                                });
                                if (cancelled || outcome === 'done') return;
                            } catch (err) {
                                console.error('[backup] silent restore resume failed:', err.message);
                                track('backup_restore_missing_failed', {
                                    auto: true, phase: 'auto_resume',
                                    error: String(err.message || err).slice(0, 200),
                                });
                                if (cancelled) return;
                            }
                            // fallback / failure → the visible prompt takes over.
                        }
                        setOffer({ kind: 'resume', missing, filesTotal: preview.files?.total || 0 });
                        track('backup_restore_prompt_shown', { kind: 'resume' });
                        return;
                    }
                    // Nothing left to bring back — the marker has served its
                    // purpose (or the missing files came home another way).
                    backupApi.setEnabled({ restorePending: false }).catch(() => {});
                    if (!freshCandidate) return;
                }
                // Same facts the off-state Backup hero shows — one summarizer
                // (summarizeCloudBackup) so the prompt and the hero can never
                // describe the same cloud differently.
                const facts = summarizeCloudBackup(preview);
                const cloudSnapshots = [
                    ...(preview.tools || []),
                    ...(preview.model ? [preview.model] : []),
                ];
                if (!facts || !shouldOfferRestore({
                    signedIn: true, settings, snapshots: cloudSnapshots, filesTotal: facts.filesTotal,
                })) return;
                setOffer({ kind: 'found', ...facts });
                track('backup_restore_prompt_shown', { kind: 'found' });
            } catch {
                // Best-effort — a failed probe just means no prompt.
            }
        };
        probe();
        return () => { cancelled = true; clearTimeout(retryTimer); };
    }, [backupApi, isAuthenticated, userId]);

    // Either way out sets the matching durable flag, so neither variant nags:
    // 'found' silences itself forever; 'resume' silences until another restore
    // is interrupted.
    const accept = () => {
        track('backup_restore_prompt_accepted', { kind: offer?.kind || 'found' });
        if (offer?.kind !== 'resume') {
            backupApi?.setEnabled({ restorePromptDismissed: true }).catch(() => {});
        }
        setOffer(null);
        setShowRestore(true);
    };
    const dismiss = () => {
        track('backup_restore_prompt_dismissed', { kind: offer?.kind || 'found' });
        backupApi?.setEnabled(offer?.kind === 'resume'
            ? { restorePending: false }
            : { restorePromptDismissed: true }).catch(() => {});
        setOffer(null);
    };

    if (!backupApi) return null;

    const isResume = offer?.kind === 'resume';
    const title = isResume ? 'Your restore didn’t finish' : 'We found your backup';
    const resumeSub = isResume
        ? `${offer.missing.toLocaleString()} of ${(offer.filesTotal || offer.missing).toLocaleString()} files are still waiting — pick up where you left off. Files already restored are kept.`
        : null;

    const subParts = offer && !isResume ? [
        offer.playlists > 0
            ? `${offer.playlists.toLocaleString()} ${offer.playlists === 1 ? 'playlist' : 'playlists'}`
            : (offer.toolCount > 0 ? `${offer.toolCount} DJ ${offer.toolCount === 1 ? 'app' : 'apps'}` : null),
        offer.filesTotal > 0 ? `${offer.filesTotal.toLocaleString()} ${offer.filesTotal === 1 ? 'file' : 'files'}` : null,
    ].filter(Boolean) : [];
    const subSuffix = offer ? [
        offer.hostname ? `from ${offer.hostname}` : null,
        offer.lastAt ? `backed up ${formatTimeAgo(offer.lastAt) || 'recently'}` : null,
    ].filter(Boolean).join(', ') : '';

    return (
        <>
            {offer && (
                <div className="rfp-overlay">
                    <div className="rfp-card" role="dialog" aria-label={title}>
                        <div className="rfp-icon">
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 17.6A4.5 4.5 0 0 0 17.5 9.4a6 6 0 0 0-11.6 1.6A4 4 0 0 0 6 19h11" />
                                <path d="M12 11v7M9.5 15.5L12 18l2.5-2.5" />
                            </svg>
                        </div>
                        <div className="rfp-title">{title}</div>
                        <div className="rfp-sub">
                            {isResume ? resumeSub : (
                                <>
                                    {subParts.join(' · ')}
                                    {subSuffix ? ` — ${subSuffix}` : ''}
                                </>
                            )}
                        </div>
                        <div className="rfp-actions">
                            <button type="button" className="rfp-quiet-btn" onClick={dismiss}>Not now</button>
                            <button type="button" className="rfp-primary-btn" onClick={accept}>
                                {isResume ? 'Continue restore…' : 'Restore…'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <BackupRestoreModal show={showRestore} onHide={() => setShowRestore(false)} />
        </>
    );
}

export default RestoreFoundPrompt;
